import React, {useCallback, useEffect, useRef, useState} from "react";
import {HalfwayContext} from "./HalfwayContext";
import {ICourseInfo} from "../core/interfaces/ICourseInfo";
import {IMenuCategory} from "../core/interfaces/IMenuCategory";
import {IMenuItem} from "../core/interfaces/IMenuItem";
import {IOptionSet} from "../core/interfaces/IOptionSet";
import {
    firestore,
    getCourseInfoFromFirestore,
    getFirestoreRef,
    getSaveOrderBasketItemsFirestoreRef
} from "../core/database/firebase";
import {resolveGolferFields, snapshotGolferDetails, validateGolferDetails} from "../core/helpers/golferFields";
import {createOrderPayload, IOrderBasketItem, IOrderInfo} from "../core/interfaces/IOrderInfo";
import {refreshMenuItemOptions, getBasketOptionIssue} from "../core/helpers/menuItemOptions";
import {PaymentEnum} from "../core/enums/PaymentEnum";
import {mapOrderToFinalOrder} from "../core/interfaces/dto/IFinalOrder";
import {collection, doc, runTransaction, Transaction, DocumentReference, DocumentData, onSnapshot, writeBatch, Unsubscribe} from "firebase/firestore";
import {getSaveOrderInfoRef} from "../core/services/api-service";
import {useNavigate} from "react-router-dom";
import {getMenuItemLinePrice, getMenuItemUnitPrice} from "../core/helpers/menuItemPricing";
import {
    findMissingRequiredOptionSet,
    resolveOptionSetRequired,
    resolveOptionSelectionMode
} from "../core/helpers/optionSetSelection";

export const HalfwayProvider = (props: any) =>
{
    // region Hooks
    const [courseLoading, setCourseLoading] = useState<boolean>(true);

    const [courseId, setCourseId] = useState<string>('');
    const [menuItems, setMenuItems] = useState<Array<IMenuItem>>([]);
    const [categories, setCategories] = useState<Array<IMenuCategory>>([]);
    const [totalItemsInBasket, setTotalItemsInBasket] = useState<number>(0);
    const [totalCostOfItemsInBasket, setTotalCostOfItemsInBasket] = useState<number>(0);
    const [courseInfo, setCourseInfo] = useState<ICourseInfo>({});
    const [courseSection, setCourseSection] = useState<string>('');
    const [onCourse, setOnCourse] = useState<boolean>(false);
    const [courseHole, setCourseHole] = useState<number | null>(null);

    // Product drawer states
    const [productDrawerMenuItemState, setProductDrawerMenuItemState] = useState<IMenuItem>({totalInBasket: 0, active: true});
    const [showProductDrawer, setShowProductDrawer] = useState<boolean>(false);
    const [hideProductDrawer] = useState<boolean>(true);
    const productDrawerMenuItemRef = useRef<IMenuItem>(productDrawerMenuItemState);
    const setProductDrawerMenuItem: React.Dispatch<React.SetStateAction<IMenuItem>> = (value) => {
        setProductDrawerMenuItemState(previousItem => {
            const nextItem = typeof value === 'function'
                ? value(previousItem)
                : value;

            productDrawerMenuItemRef.current = nextItem;
            return nextItem;
        });
    };

    // Basket drawer states
    const [showBasketDrawer, setShowBasketDrawer] = useState<boolean>(false);
    const [basketItems] = useState<Array<IMenuItem>>([]);
    const [savingOrder, setSavingOrder] = useState<boolean>(false);
    const [orderSaveError, setOrderSaveError] = useState<string>('');
    const [userName, setUserName] = useState<string>('');
    const [userPhone, setUserPhone] = useState<string>('');
    const [customGolferValues, setCustomGolferValues] = useState<Record<string, string>>({});
    const golferFields = resolveGolferFields(courseInfo?.golferFields);
    const golferFieldValues = {...customGolferValues, name: userName};
    const setGolferFieldValue = (id: string, value: string) => {
        if (id === 'name') setUserName(value);
        else setCustomGolferValues(previous => ({...previous, [id]: value}));
        setOrderSaveError('');
    };
    const [orderIsSaved, setOrderIsSaved] = useState<boolean>(false);
    const [optionSetMap, setOptionSetMap] = useState<Record<string, IOptionSet>>({});
    const optionSetMapRef = useRef(optionSetMap);
    optionSetMapRef.current = optionSetMap;
    const navigate = useNavigate();

    //endregion

    // region Firebase Fetch Data
    const loadedStateRef = useRef({
        courseInfo: false,
        menuItems: false,
        categories: false
    });

    const getCourseInfoFromApi = async (courseId: string) =>
    {
        const courseInfo: ICourseInfo = await getCourseInfoFromFirestore(courseId);
        setCourseInfo(courseInfo);
        loadedStateRef.current.courseInfo = true;
        toggleLoader();
    }

    const resetCourseData = () =>
    {
        loadedStateRef.current = {
            courseInfo: false,
            menuItems: false,
            categories: false
        };
        otherAddedRef.current = false;
        setCourseLoading(true);
        setCourseInfo({});
        setUserName('');
        setUserPhone('');
        setCustomGolferValues({});
        setOrderSaveError('');
        setCategories([]);
        setMenuItems([]);
        setOptionSetMap({});
        setProductDrawerMenuItem({totalInBasket: 0, active: true});
        setShowProductDrawer(false);
        setShowBasketDrawer(false);
        updateBasketTotals([]);
    }

    const mapCategory = (itemId: string, mItem: DocumentData): IMenuCategory =>
    {
        return {
            id: itemId,
            name: mItem.name,
            isDeleted: mItem.deleted
        };
    }

    const otherAddedRef = useRef(false);
    const getCategoriesFromApi = (courseId: string): Unsubscribe =>
    {
        const courseCollectionName = 'courses';
        const categoryCollectionName = 'categoryCollection';
        const fire = getFirestoreRef();

        const ref = collection(fire, courseCollectionName, courseId, categoryCollectionName);

        const otherCategory: IMenuCategory =
            {
                id: '',
                name: 'Other',
                isDeleted: false,
                isSelected: false
            };

        if (!otherAddedRef.current)
        {
            otherAddedRef.current = true;
            setCategories(oldItems =>
            {
                return [...oldItems, otherCategory];
            })
        }

        return onSnapshot(ref, snapshot =>
        {
            snapshot.docChanges().forEach((change) =>
            {
                if (change.type === 'added')
                {
                    const newItem = mapCategory(change.doc.id, change.doc.data());
                    setCategories(oldItems =>
                    {
                        if (oldItems.findIndex(x => x.id === newItem.id) > -1)
                        {
                            return [...oldItems].sort((a, b) => (a?.name ?? '' ).localeCompare(b?.name ?? ''));
                        }
                        else
                        {
                            const newList = [...oldItems, newItem];
                            newList.sort((a, b) => (a?.name ?? '' ).localeCompare(b?.name ?? ''))
                            return newList;
                        }
                    });
                }
                else if (change.type === 'modified')
                {
                    const modifiedItem = mapCategory(change.doc.id, change.doc.data());
                    if (!modifiedItem.isDeleted)
                    {
                        setCategories(oldItems =>
                        {
                            const indexAt = oldItems.findIndex(search => search.id === modifiedItem.id);
                            if (indexAt === -1) {
                                return oldItems;
                            }

                            const newItems = [...oldItems];
                            newItems[indexAt] = {
                                ...newItems[indexAt],
                                isDeleted: false,
                                name: modifiedItem.name
                            };
                            newItems.sort((a, b) => (a?.name ?? '' ).localeCompare(b?.name ?? ''))
                            return newItems;
                        })
                    }
                    else
                    {
                        setCategories(oldItems =>
                        {
                            const indexAt = oldItems.findIndex(search => search.id === modifiedItem.id);
                            if (indexAt !== -1)
                            {
                                const newItems = [...oldItems];
                                newItems[indexAt] = modifiedItem;
                                newItems.sort((a, b) => (a?.name ?? '' ).localeCompare(b?.name ?? ''))
                                return newItems;
                            }

                            return [...oldItems].sort((a, b) => (a?.name ?? '' ).localeCompare(b?.name ?? ''));
                        });
                    }
                }
                else if (change.type === 'removed')
                {
                    setCategories(oldItems => oldItems.filter(item => item.id !== change.doc.id));
                }
            });

            loadedStateRef.current.categories = true;
            toggleLoader();
        })
    }

    const getMenuItemsFromApi = (courseId: string): Unsubscribe =>
    {
        const courseCollectionName = 'courses';
        const categoryCollectionName = 'menuItemCollection';

        const fire = getFirestoreRef();
        const ref = collection(fire, courseCollectionName, courseId, categoryCollectionName);

        return onSnapshot(ref, snapshot => {
            snapshot.docChanges().forEach((change) => {

                if (change.type === 'added')
                {
                    const newItem = mapMenuItem(change.doc.id, change.doc.data());

                    setMenuItems(oldItems =>
                    {
                        if (oldItems.findIndex(x => x.id === newItem.id) > -1)
                        {
                            return oldItems;
                        }
                        else
                        {
                            return [...oldItems, newItem];
                        }
                    });
                }
                if (change.type === 'modified')
                {
                    const modifiedItem = mapMenuItem(change.doc.id, change.doc.data());
                    if (!modifiedItem.active)
                    {
                        setMenuItems(oldItems =>
                        {
                            const indexAt = oldItems.findIndex(search => search.id === modifiedItem.id);
                            if (indexAt === -1) {
                                return oldItems;
                            }

                            const newItems = [...oldItems];
                            newItems[indexAt] = {
                                ...newItems[indexAt],
                                active: false,
                                totalInBasket: 0
                            };

                            return newItems;
                        })
                    }
                    else
                    {
                        setMenuItems(oldItems => {
                            const indexAt = oldItems.findIndex(search => search.id === modifiedItem.id);
                            if (indexAt !== -1) {
                                const existingItem = oldItems[indexAt];
                                modifiedItem.totalInBasket = modifiedItem.active ? existingItem.totalInBasket : 0;
                                modifiedItem.selectedOptions = modifiedItem.active ? existingItem.selectedOptions : [];
                                modifiedItem.specialInstruction = modifiedItem.active ? existingItem.specialInstruction : '';
                                const newItems = [...oldItems];
                                newItems[indexAt] = refreshMenuItemOptions({
                                    ...modifiedItem, optionAvailabilityChanged: existingItem.optionAvailabilityChanged
                                }, optionSetMapRef.current);
                                return newItems;
                            }
                            return oldItems;
                        });
                    }
                }
                if (change.type === 'removed')
                {
                    setMenuItems(oldItems => oldItems.filter(item => item.id !== change.doc.id));
                }
            });

            loadedStateRef.current.menuItems = true;
            toggleLoader();
        })
    }

    const mapMenuItem = (itemId: string, mItem: DocumentData): IMenuItem =>
    {
        return refreshMenuItemOptions({
            id: itemId,
            name: mItem.name,
            description: mItem.description,
            price: Number(mItem.price),
            basePrice: Number(mItem.price),
            isDeleted: mItem.isDeleted ?? false,
            imageUrl: '',
            totalInBasket: 0,
            categoryId: mItem.categoryId,
            isPopular: mItem.isPopular ?? false,
            optionSetIds: Array.isArray(mItem.optionSets) ? mItem.optionSets : [],
            selectedOptions: [],
            active: mItem.active,
            onCourse: mItem.onCourse ?? false
        }, optionSetMapRef.current);
    }

    const mapOptionSet = (optionSetId: string, data: DocumentData): IOptionSet =>
    {
        return {
            id: optionSetId,
            name: data.name,
            deleted: data.deleted ?? false,
            selectionMode: resolveOptionSelectionMode(data.selectionMode),
            isRequired: resolveOptionSetRequired(data.isRequired),
            options: Array.isArray(data.options)
                ? data.options
                    .filter((option: any) => !(option?.deleted ?? false))
                    .map((option: any, index: number) => ({
                        id: option.id ?? `option-${index}`,
                        name: option.name,
                        additionalPrice: Number(option.additionalPrice ?? 0),
                        deleted: option.deleted ?? false,
                        active: option.active !== false
                    }))
                : []
        };
    }

    const getOptionSetsFromApi = (courseId: string): Unsubscribe =>
    {
        const fire = getFirestoreRef();
        const ref = collection(fire, 'courses', courseId, 'optionSetCollection');

        return onSnapshot(ref, snapshot =>
        {
            setOptionSetMap(currentMap =>
            {
                const nextMap = {...currentMap};

                snapshot.docChanges().forEach(change =>
                {
                    if (change.type === 'removed')
                    {
                        delete nextMap[change.doc.id];
                        return;
                    }

                    nextMap[change.doc.id] = mapOptionSet(change.doc.id, change.doc.data());
                });

                return nextMap;
            });
        });
    }

    const enrichMenuItemWithOptionSets = (menuItem: IMenuItem, selectDefaults = false): IMenuItem =>
        refreshMenuItemOptions(menuItem, optionSetMap, selectDefaults);

    useEffect(() => {
        setMenuItems(items => items.map(item => refreshMenuItemOptions(item, optionSetMap)));
        setProductDrawerMenuItem(item => refreshMenuItemOptions(item, optionSetMap));
    }, [optionSetMap]);

    const toggleLoader = () =>
    {
        if (loadedStateRef.current.courseInfo &&
            loadedStateRef.current.categories &&
            loadedStateRef.current.menuItems)
        {
            setCourseLoading(false);
        }
    }

    // endregion

    // region Firebase Save Data

    const postOrderToFirebase = async () =>
    {
        if (savingOrder) {
            return;
        }

        setSavingOrder(true);
        setOrderSaveError('');

        const golferError = validateGolferDetails(golferFields, golferFieldValues);
        if (golferError)
        {
            setOrderSaveError(golferError);
            setSavingOrder(false);
            return;
        }

        if (totalItemsInBasket === 0)
        {
            setOrderSaveError('Please add at least one item to your basket.');
            setSavingOrder(false);
            return;
        }

        if (onCourse && courseHole === null)
        {
            setOrderSaveError('This on-course QR code does not contain a valid hole number. Please scan the QR code again.');
            setSavingOrder(false);
            return;
        }

        let confirmedOptionSets: Record<string, IOptionSet> | undefined;
        try {
            const basket = menuItems.filter(item => item.totalInBasket > 0);
            const optionSetIds = Array.from(new Set(basket.flatMap(item => item.optionSetIds ?? [])));
            const orderRef = doc(getSaveOrderInfoRef(courseId));
            const saveCheckout = async (write: (ref: DocumentReference, data: DocumentData) => void, transaction?: Transaction) => {
                let latestOptionSets = optionSetMap;
                if (transaction) {
                    const snapshots = await Promise.all(optionSetIds.map(id =>
                        transaction.get(doc(getFirestoreRef(), 'courses', courseId, 'optionSetCollection', id))));
                    latestOptionSets = {};
                    snapshots.forEach(snapshot => {
                        if (snapshot.exists()) latestOptionSets[snapshot.id] = mapOptionSet(snapshot.id, snapshot.data());
                    });
                    confirmedOptionSets = latestOptionSets;
                }
                const checkoutItems = basket.map(item => refreshMenuItemOptions(item, latestOptionSets));
                const optionIssue = checkoutItems.map(getBasketOptionIssue).find(Boolean);
                if (optionIssue) {
                    throw new Error(optionIssue);
                }

                const orderBasketItems: IOrderBasketItem[] = checkoutItems
                    .filter(x => (x?.totalInBasket ?? 0) > 0)
                    .map(menuItem =>
                    {
                        const unitPrice = getMenuItemUnitPrice(menuItem);
                        const quantity = menuItem.totalInBasket ?? 0;

                        return {
                            menuItemId: menuItem.id ?? '',
                            categoryId: menuItem.categoryId ?? '',
                            name: menuItem.name ?? '',
                            quantity,
                            basePrice: Number(menuItem.basePrice ?? menuItem.price ?? 0),
                            unitPrice,
                            lineTotal: unitPrice * quantity,
                            specialInstruction: menuItem.specialInstruction ?? '',
                            selectedOptions: (menuItem.selectedOptions ?? []).map(option => ({
                                optionSetId: option.optionSetId ?? '',
                                optionSetName: option.optionSetName ?? '',
                                optionId: option.optionId ?? '',
                                optionName: option.optionName ?? '',
                                additionalPrice: Number(option.additionalPrice ?? 0)
                            }))
                        };
                    });

                const orderCost = orderBasketItems.reduce((partialSum, basketItem) => {
                    return partialSum + basketItem.lineTotal
                }, 0);

                const order: IOrderInfo = createOrderPayload(
                    userPhone,
                    '',
                    golferFields.some(field => field.id === 'name') ? userName.trim() : '',
                    orderBasketItems,
                    orderCost,
                    0,
                    courseId,
                    courseSection,
                    onCourse,
                    courseHole ?? undefined,
                    PaymentEnum.PayAtHouse,
                    false
                );

                order.golferDetails = snapshotGolferDetails(golferFields, golferFieldValues);

                const finalOrder = mapOrderToFinalOrder(order);
                write(orderRef, finalOrder);

                const basketItemsRef = getSaveOrderBasketItemsFirestoreRef(courseId, orderRef.id);
                order.basketItems.forEach(basketItem =>
                {
                    const itemRef = doc(basketItemsRef);
                    write(itemRef, {
                        ...basketItem,
                        orderId: orderRef.id
                    });
                });

            };

            if (optionSetIds.length > 0) {
                // Reading the option documents and writing the order in the same
                // transaction retries validation if availability changes during checkout.
                await runTransaction(firestore, transaction => saveCheckout((ref, data) => transaction.set(ref, data), transaction));
            } else {
                const batch = writeBatch(firestore);
                await saveCheckout((ref, data) => batch.set(ref, data));
                await batch.commit();
            }
            setOrderIsSaved(true);
            navigate(`/success?${new URLSearchParams({courseId, orderId: orderRef.id})}`);
        }
        catch (error)
        {
            setOrderIsSaved(false);
            const issue = confirmedOptionSets && menuItems.map(item => getBasketOptionIssue(refreshMenuItemOptions(item, confirmedOptionSets!))).find(Boolean);
            setOrderSaveError(issue || 'We could not place your order. Please try again.');
        }
        finally
        {
            if (confirmedOptionSets) {
                const confirmed = confirmedOptionSets;
                setOptionSetMap(current => {
                    const next = {...current};
                    menuItems.flatMap(item => item.optionSetIds ?? []).forEach(id => {
                        // Never replace a newer live update with the checkout snapshot.
                        if (current[id] !== optionSetMap[id]) return;
                        if (confirmed[id]) next[id] = confirmed[id];
                        else delete next[id];
                    });
                    return next;
                });
            }
            setSavingOrder(false);
        }
    }

    const postMenuItemsToFirebase = async (order: IOrderInfo, orderId: string) =>
    {
        const basketItemsRef = getSaveOrderBasketItemsFirestoreRef(courseId, orderId);
        const batch = writeBatch(firestore);
        if (!order.basketItems)
            return;

        order.basketItems.forEach(basketItem =>
        {
            basketItem.orderId = orderId;
            const itemRef = doc(basketItemsRef);
            batch.set(itemRef, basketItem);
        });

        await batch.commit();
    }

    // endregion

    // region Product Slider Drawer logic

    const toggleProductDrawer = (menuItem: IMenuItem, overrideDisplay: boolean = false) =>
    {
        if (!overrideDisplay)
        {
            setShowProductDrawer(!showProductDrawer);
        }
        else
        {
            setShowProductDrawer(overrideDisplay);
        }

        setProductDrawerMenuItem(enrichMenuItemWithOptionSets(menuItem, menuItem.totalInBasket === 0 && !menuItem.optionAvailabilityChanged));
    }

    // endregion

    // region Basket Slider Drawer logic

    const toggleBasketDrawer = () =>
    {
        setShowBasketDrawer(!showBasketDrawer);
    }

    // endregion

    // region Basket Flow

    const updateBasketTotals = useCallback((items: IMenuItem[]) => {
        const totalItems = items.reduce((partialSum, menuItem) => partialSum + menuItem.totalInBasket,0);

        const cost = items.reduce((partialSum, menuItem) => {
            return partialSum + getMenuItemLinePrice(menuItem)
        }, 0);

        setTotalItemsInBasket(Number(totalItems));
        setTotalCostOfItemsInBasket(Number(cost));
    }, [])

    const addToBasketFunction = (menuItem: IMenuItem) => {
        const latestMenuItem = enrichMenuItemWithOptionSets(menuItem ?? productDrawerMenuItemRef.current);
        if (latestMenuItem.totalInBasket > 0 && findMissingRequiredOptionSet(
            latestMenuItem.optionSets ?? [],
            latestMenuItem.selectedOptions ?? []
        )) {
            return;
        }

        const basketMenuItem: IMenuItem = {
            ...latestMenuItem,
            optionAvailabilityChanged: false,
            selectedOptions: latestMenuItem.totalInBasket > 0 ? latestMenuItem.selectedOptions : [],
            specialInstruction: latestMenuItem.totalInBasket > 0 ? latestMenuItem.specialInstruction : ''
        };
        const itemIndex: number = menuItems.findIndex(x => x.id === basketMenuItem.id);

        if (itemIndex === -1 || !menuItems[itemIndex].active)
        {
            setShowProductDrawer(false);
            return;
        }

        updateMenuItems(basketMenuItem, itemIndex);
        setShowProductDrawer(false);
    }

    useEffect(() => {
        updateBasketTotals(menuItems);
    }, [menuItems, updateBasketTotals]);

    const updateMenuItems = (item: IMenuItem, index: number) => {
        setMenuItems((existingItems): (IMenuItem)[] => {
            const nextItems = [
                ...existingItems.slice(0, index),
                item,
                ...existingItems.slice(index + 1),
            ];

            updateBasketTotals(nextItems);

            return nextItems;
        })
    }

    // endregion

    const provider =
        {
            courseLoading, setCourseLoading,

            courseId, setCourseId,
            courseSection, setCourseSection,
            onCourse, setOnCourse,
            courseHole, setCourseHole,
            menuItems, setMenuItems,
            categories, setCategories,
            totalItemsInBasket, setTotalItemsInBasket,
            totalCostOfItemsInBasket, setTotalCostOfItemsInBasket,
            courseInfo, setCourseInfo,

            // Product Drawer
            productDrawerMenuItem: productDrawerMenuItemState, setProductDrawerMenuItem,
            showProductDrawer, setShowProductDrawer,
            hideProductDrawer,
            toggleProductDrawer,
            addToBasketFunction,

            // Basket Drawer
            showBasketDrawer, setShowBasketDrawer,
            basketItems,
            savingOrder,
            orderSaveError,
            userName, setUserName,
            golferFields, golferFieldValues, setGolferFieldValue,
            userPhone, setUserPhone,
            orderIsSaved,
            setOrderIsSaved,

            postOrderToFirebase,
            postMenuItemsToFirebase,
            toggleBasketDrawer,

            getCourseInfoFromApi,
            getCategoriesFromApi,
            getMenuItemsFromApi,
            getOptionSetsFromApi,
            resetCourseData
        }

    return (
        <HalfwayContext.Provider value={provider}>
            {props.children}
        </HalfwayContext.Provider>
    )
}
